import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import { getAuthToken } from '../../lib/supabaseClient';
import { apiFetch, ApiError } from '../../lib/apiClient';
import { Logo } from '../ui/Logo';
import { SUPPORT_EMAIL } from '../../lib/authRedirects';
import { hapticWarning } from '../../lib/haptics';
import { AiRecommendationsAccountControl } from './AiRecommendationsAccountControl';

export function AccountPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [confirmingDelete, deleteText]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteText.trim().toUpperCase() !== 'DELETE') {
      setError('Type DELETE to confirm.');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');

      await apiFetch('/api/delete-account', {
        method: 'POST',
        token,
        body: { confirm: 'DELETE' },
      });

      await signOut().catch(() => undefined);
      navigate('/');
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not delete account';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  const email = user?.email || '—';
  const name =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.first_name ||
    email.split('@')[0];

  return (
    <div className="grain min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-foreground/90 hover:text-white"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Logo />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pb-20 pt-10">
        <h1 className="display animate-rise mb-2 text-3xl font-semibold">Account</h1>
        <p className="animate-rise delay-75 mb-8 text-muted-foreground">
          Manage your MovieMe profile and data.
        </p>

        <section className="cine-card animate-rise delay-100 mb-6 rounded-2xl p-6">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Signed in as</p>
          <p className="mt-1 text-lg font-medium text-foreground">{name}</p>
          <p className="text-sm text-muted-foreground">{email}</p>
        </section>

        <AiRecommendationsAccountControl />

        <section className="cine-card animate-rise delay-125 mb-6 rounded-2xl p-6">
          <h2 className="mb-1 text-lg font-semibold text-foreground">Taste profile</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Soon you’ll save favorite movies, rate what you’ve seen, and shape better
            recommendations from here — so Discover burns fewer searches on titles you already know.
          </p>
          <p className="mt-3 text-xs font-medium uppercase tracking-wide text-primary/80">
            Coming next
          </p>
        </section>

        <section className="cine-card animate-rise delay-150 mb-6 space-y-3 rounded-2xl p-6">
          <Link to="/privacy" className="block text-foreground/90 transition hover:text-white">
            Privacy Policy
          </Link>
          <Link to="/terms" className="block text-foreground/90 transition hover:text-white">
            Terms of Service
          </Link>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="block text-foreground/90 transition hover:text-white"
          >
            Contact support
          </a>
        </section>

        <div className="animate-rise delay-200 mb-8">
          <button
            type="button"
            onClick={handleSignOut}
            className="btn-ghost inline-flex min-h-[44px] items-center gap-2 px-5 py-3"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>

        <section className="animate-rise delay-225 rounded-2xl border border-red-500/30 bg-red-950/20 p-6">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-red-200">
            <Trash2 className="h-5 w-5" />
            Delete account
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-red-100/80">
            Permanently deletes your MovieMe profile, watchlist, watched history, and search usage.
            This cannot be undone.
          </p>

          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => {
                void hapticWarning();
                setConfirmingDelete(true);
              }}
              className="min-h-[44px] rounded-full border border-red-400/50 px-5 py-2.5 text-sm font-medium text-red-200 transition hover:bg-red-500/20"
            >
              Delete my account…
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-red-100/90">
                Type <span className="font-semibold text-white">DELETE</span> to confirm.
              </p>
              <input
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                className="input-dark w-full"
                placeholder="DELETE"
                autoComplete="off"
                aria-label="Type DELETE to confirm account deletion"
              />
              {error && (
                <p className="text-sm text-red-300" role="alert">
                  {error}
                </p>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleDeleteAccount}
                  className="min-h-[44px] rounded-full bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-500 disabled:opacity-60"
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => {
                    setConfirmingDelete(false);
                    setDeleteText('');
                  }}
                  className="btn-ghost min-h-[44px] px-5 py-2.5 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
